import React from 'react';
import { Table, Tag, Tooltip, Typography } from 'antd';
import type { TableColumnsType as ColumnsType } from 'antd';

import {
  curatedRowsLine,
  DATA_STATUS_COLOURS,
  dataBadgeLabel,
  GAP_KIND_LABELS,
  GAP_OWNER_NOTES,
  hasSavedExtras,
  isBlocker,
  plural,
  readable,
  sortGaps,
  turnsLabel,
} from './format';
import {
  DataCoverage,
  DataStatus,
  Gap,
  InstrumentCoverage,
  NewsCoverage,
  PreviewCheck,
  PriceCoverage,
  SavedState,
} from './types';

/* ------------------------------------------------------------------ *
 * The badge (cards and the drawer)
 * ------------------------------------------------------------------ */

/**
 * "Data complete" or "Data gaps: N (M blocking)". Renders nothing for an
 * unknown status: a card built before the data check must not look checked.
 */
export const DataBadge: React.FC<{
  status: DataStatus | null | undefined;
  gapCount?: number;
  blockers?: number;
}> = ({ status, gapCount = 0, blockers = 0 }) => {
  const label = dataBadgeLabel(status, gapCount, blockers);
  if (!label || !status) return null;
  return (
    <Tooltip
      title={
        status === 'complete'
          ? 'Every instrument has prices, is tradable and graded, and has news in every turn.'
          : 'Open the preview for each gap and what to add.'
      }
    >
      <Tag color={DATA_STATUS_COLOURS[status]} data-testid="data-badge">
        {label}
      </Tag>
    </Tooltip>
  );
};

/* ------------------------------------------------------------------ *
 * Gaps
 * ------------------------------------------------------------------ */

const OWNER_COLOURS: Record<string, string | undefined> = {
  ours: 'blue',
  'PM (Tudor)': 'purple',
  data: 'cyan',
};

/**
 * Each gap with what it is, the turns it touches, what to add and whose lane
 * that is. Blockers come first: they are the ones that stop a save. `compact`
 * is the denser form a chat message uses under its text.
 */
export const GapList: React.FC<{ gaps: Gap[]; compact?: boolean }> = ({
  gaps,
  compact = false,
}) =>
  gaps.length ? (
    <ol
      className={`blueprint-builder__gaps${
        compact ? ' blueprint-builder__gaps--compact' : ''
      }`}
    >
      {sortGaps(gaps).map((gap, index) => {
        const turns = turnsLabel(gap.turns);
        return (
          <li
            // Gaps carry no id; the sorted position is stable per render.
            key={`${gap.kind}-${gap.code || 'window'}-${index}`}
            className={`blueprint-builder__gap blueprint-builder__gap--${
              isBlocker(gap) ? 'blocker' : 'warning'
            }`}
            data-testid="data-gap"
          >
            <div className="blueprint-builder__gap-head">
              <Tag color={isBlocker(gap) ? 'red' : 'gold'}>
                {isBlocker(gap) ? 'blocker' : 'warning'}
              </Tag>
              <Tag>{GAP_KIND_LABELS[gap.kind] || readable(gap.kind || '')}</Tag>
              <Typography.Text strong>
                {gap.code || 'whole window'}
              </Typography.Text>
              {turns && (
                <Typography.Text type="secondary">{turns}</Typography.Text>
              )}
            </div>
            {gap.detail && (
              <div className="blueprint-builder__gap-detail">{gap.detail}</div>
            )}
            {gap.remedy && (
              <div className="blueprint-builder__gap-remedy">
                <Typography.Text type="secondary">
                  What to add:{' '}
                </Typography.Text>
                <span>{gap.remedy}</span>{' '}
                {gap.owner && (
                  <Tooltip title={GAP_OWNER_NOTES[gap.owner]}>
                    <Tag color={OWNER_COLOURS[gap.owner]}>{gap.owner}</Tag>
                  </Tooltip>
                )}
              </div>
            )}
          </li>
        );
      })}
    </ol>
  ) : (
    <Typography.Text type="secondary">No gaps.</Typography.Text>
  );

/* ------------------------------------------------------------------ *
 * Per-instrument coverage
 * ------------------------------------------------------------------ */

const PRICE_TAGS: Record<
  PriceCoverage['status'],
  { label: string; colour: string }
> = {
  ok: { label: 'full', colour: 'green' },
  partial: { label: 'partial', colour: 'gold' },
  missing: { label: 'missing', colour: 'red' },
};

const NEWS_TAGS: Record<
  NewsCoverage['status'],
  { label: string; colour: string }
> = {
  ok: { label: 'ok', colour: 'green' },
  thin: { label: 'thin', colour: 'gold' },
  none: { label: 'none', colour: 'red' },
};

/** How many missing landings are named inline before "+N more". */
const MISSING_SHOWN = 3;

const PricesCell: React.FC<{ prices: PriceCoverage }> = ({ prices }) => {
  const tag = PRICE_TAGS[prices.status] || {
    label: prices.status,
    colour: 'default',
  };
  const missing = prices.weeks_missing || [];
  const range =
    prices.available_from || prices.available_to
      ? `${prices.available_from || '?'} → ${prices.available_to || '?'}`
      : 'no history';
  return (
    <div className="blueprint-builder__coverage-cell">
      <span>
        <Tag color={tag.colour}>{tag.label}</Tag>
        {prices.unit === 'yield' ? 'yields' : 'prices'}
      </span>
      <Typography.Text type="secondary">{range}</Typography.Text>
      {missing.length > 0 && (
        <Tooltip title={missing.join(', ')}>
          <Typography.Text type="danger">
            missing {missing.slice(0, MISSING_SHOWN).join(', ')}
            {missing.length > MISSING_SHOWN
              ? ` +${missing.length - MISSING_SHOWN} more`
              : ''}
          </Typography.Text>
        </Tooltip>
      )}
    </div>
  );
};

const NewsCell: React.FC<{ news: NewsCoverage }> = ({ news }) => {
  const tag = NEWS_TAGS[news.status] || {
    label: news.status,
    colour: 'default',
  };
  const thin = turnsLabel(news.thin_turns);
  const perTurn = news.per_turn || [];
  const available = news.available_per_turn || [];
  return (
    <div className="blueprint-builder__coverage-cell">
      <span>
        <Tag color={tag.colour}>{tag.label}</Tag>
        {thin && (
          <Typography.Text type="secondary">thin in {thin}</Typography.Text>
        )}
      </span>
      {perTurn.length > 0 && (
        <Tooltip title="Items on this instrument's topics that each turn serves, as in the turn's news list">
          <Typography.Text type="secondary">
            per turn {perTurn.join(' · ')}
          </Typography.Text>
        </Tooltip>
      )}
      {available.length > 0 && (
        <Tooltip title="What the archive offered on these topics, turn by turn: more on offer than served means the pick can change (boost or pin); too little means news to import">
          <Typography.Text type="secondary" data-testid="news-available">
            available {available.join(' · ')}
          </Typography.Text>
        </Tooltip>
      )}
    </div>
  );
};

const coverageColumns: ColumnsType<InstrumentCoverage> = [
  {
    title: 'Instrument',
    key: 'instrument',
    width: 130,
    render: (_: unknown, row: InstrumentCoverage) => (
      <div
        className="blueprint-builder__coverage-cell"
        data-testid={`coverage-${row.code}`}
      >
        <Typography.Text strong>{row.code}</Typography.Text>
        {row.label && row.label !== row.code && (
          <Typography.Text type="secondary">{row.label}</Typography.Text>
        )}
      </div>
    ),
  },
  {
    title: 'Prices / yields',
    key: 'prices',
    render: (_: unknown, row: InstrumentCoverage) =>
      row.prices ? <PricesCell prices={row.prices} /> : '—',
  },
  {
    title: 'Tradable',
    key: 'tradable',
    render: (_: unknown, row: InstrumentCoverage) => (
      <div className="blueprint-builder__coverage-cell">
        <span>
          {row.tradable?.ok ? (
            <Tag color="green">traded</Tag>
          ) : (
            <Tag color="orange">not traded</Tag>
          )}
        </span>
        {row.tradable?.reason && (
          <Typography.Text type="secondary">
            {row.tradable.reason}
          </Typography.Text>
        )}
      </div>
    ),
  },
  {
    title: 'Graded',
    key: 'graded',
    render: (_: unknown, row: InstrumentCoverage) => (
      <div className="blueprint-builder__coverage-cell">
        <span>
          {row.graded?.ok ? (
            <Tag color="green">
              {row.graded.column ? `graded as ${row.graded.column}` : 'graded'}
            </Tag>
          ) : (
            <Tag color="warning">ungraded</Tag>
          )}
        </span>
        {row.graded?.reason && (
          <Typography.Text type="secondary">
            {row.graded.reason}
          </Typography.Text>
        )}
      </div>
    ),
  },
  {
    title: 'News',
    key: 'news',
    render: (_: unknown, row: InstrumentCoverage) =>
      row.news ? <NewsCell news={row.news} /> : '—',
  },
];

/* ------------------------------------------------------------------ *
 * The section
 * ------------------------------------------------------------------ */

const summary = (coverage: DataCoverage) => {
  const blockers = coverage.gaps.filter(isBlocker).length;
  if (coverage.status === 'complete' && !coverage.gaps.length)
    return 'Every instrument has the data this window needs.';
  if (blockers)
    return `${plural(blockers, 'gap')} must be fixed before this can be saved; the rest are warnings.`;
  return 'Nothing stops a save; the warnings say what is missing and what to add.';
};

/**
 * What the window's data can carry, per instrument (prices or yields,
 * tradable, graded, news), then every gap with its remedy and owner. A
 * version built before the data check has no `data_status`: it is shown as
 * unknown rather than complete, because nobody measured it.
 */
export const DataCoverageSection: React.FC<{
  coverage: DataCoverage | null | undefined;
  /** The preview's `data` check, when it has one. */
  check?: PreviewCheck;
}> = ({ coverage, check }) => {
  if (!coverage) {
    return (
      <Typography.Text type="secondary" data-testid="data-coverage-unknown">
        Coverage unknown: this version was built before the data check. Ask the
        builder to rebuild it for a per-instrument report.
      </Typography.Text>
    );
  }
  const instruments = coverage.instruments || [];
  const gaps = coverage.gaps || [];
  return (
    <div className="blueprint-builder__coverage" data-testid="data-coverage">
      <Typography.Text type="secondary">
        {check?.detail || summary({ ...coverage, gaps })}
      </Typography.Text>
      {instruments.length > 0 && (
        <Table<InstrumentCoverage>
          size="small"
          pagination={false}
          rowKey="code"
          columns={coverageColumns}
          dataSource={instruments}
          scroll={{ x: 'max-content' }}
        />
      )}
      {gaps.length > 0 && (
        <div>
          <Typography.Text
            type="secondary"
            className="blueprint-builder__label"
          >
            Gaps and what to add
          </Typography.Text>
          <GapList gaps={gaps} />
        </div>
      )}
    </div>
  );
};

/* ------------------------------------------------------------------ *
 * After a save
 * ------------------------------------------------------------------ */

/**
 * What the save reported beyond its message: the instruments it left out (and
 * why) and how many AI Curated rows the blueprint serves. Each part renders
 * only when the save reported it; saves from before v2 report neither.
 */
export const SavedExtras: React.FC<{ saved: SavedState }> = ({ saved }) => {
  if (!hasSavedExtras(saved)) return null;
  const excluded = saved.excluded_instruments || [];
  const curated = saved.curated_rows;
  return (
    <div className="blueprint-builder__saved-extras" data-testid="saved-extras">
      {typeof curated === 'number' && <div>{curatedRowsLine(curated)}</div>}
      {excluded.length > 0 && (
        <div>
          Not included:
          <ul>
            {excluded.map(item => (
              <li key={item.code}>
                <strong>{item.code}</strong>
                {item.reason ? `: ${item.reason}` : ''}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};
